import _defineProperty from "/app/node_modules/.pnpm/next@13.5.3_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { createSlice } from '@reduxjs/toolkit';
import { remove } from 'lodash';
import { arrayMove } from '@dnd-kit/sortable';
import uuid from 'uuid';
import { Screen } from 'constants/tracking/screens';
import { actions as mediaUploadActions } from 'state/media-upload/slice';
import { UiState as UiStateEnum } from 'constants/ui';
import { initialEntityStructure, setNormalizedData } from 'libs/utils/redux';
import { stateName, FieldName } from './constants';
const initialItemAttributes = {
  id: null,
  assignedPhotos: [],
  catalogId: null,
  colorIds: [],
  title: '',
  description: '',
  statusId: null,
  sizeId: null,
  price: null,
  isUnisex: false,
  isPromoted: false,
  brandId: null,
  brandTitle: null,
  isbn: null,
  packageSize: {
    id: null,
    custom: {
      domestic: null,
      international: null
    }
  },
  catalog: {
    attributes: {
      byName: {},
      names: []
    }
  },
  videoGameRatingId: null,
  alertType: null,
  isPushUpChecked: false,
  feedbackId: null,
  measurementLength: null,
  measurementWidth: null
};
const initialFormDataState = {
  brands: _objectSpread(_objectSpread({}, initialEntityStructure), {}, {
    suggested: initialEntityStructure,
    isLuxury: null
  }),
  colors: _objectSpread(_objectSpread({}, initialEntityStructure), {}, {
    suggested: initialEntityStructure
  }),
  catalogs: _objectSpread(_objectSpread({}, initialEntityStructure), {}, {
    initialIds: [],
    suggested: []
  }),
  sizes: _objectSpread(_objectSpread({}, initialEntityStructure), {}, {
    suggested: initialEntityStructure
  }),
  statuses: initialEntityStructure,
  shippingOptions: [],
  sizeGroups: initialEntityStructure,
  photoTips: initialEntityStructure,
  videoGameRatings: initialEntityStructure,
  priceSuggestions: {
    min: null,
    max: null,
    currency: null
  },
  dynamicAttributes: {
    byName: {}
  },
  bookDetails: null,
  shouldShowSizeModal: false,
  tempUuid: uuid.v4()
};
const initialUiState = {
  screenName: Screen.ItemUpload,
  uiState: UiStateEnum.Pending,
  errors: {
    byName: {},
    names: []
  },
  isLuxuryItemModalOpen: false,
  showMissingPostalCode: false,
  shippingOptions: {
    uiState: UiStateEnum.Idle,
    isModalOpen: false
  },
  isFormDisabled: false,
  price: {
    hasSimilarItems: false,
    isSimilarItemsModalOpen: false,
    displayType: null
  },
  color: {
    uiState: UiStateEnum.Idle
  },
  containsWebPhoto: false,
  isOfflineVerificationModalOpen: false,
  isAuthenticityProofModalOpen: false,
  authenticityModal: {
    isOpen: false,
    content: null
  },
  canBumpItem: false,
  dynamicAttributes: {
    uiState: UiStateEnum.Idle
  },
  isbn: {
    uiState: UiStateEnum.Idle
  }
};
export const initialState = {
  previousAttributes: initialItemAttributes,
  attributes: initialItemAttributes,
  formData: initialFormDataState,
  ui: initialUiState,
  isOfflineVerificationEligible: false,
  configuration: {
    catalogFieldsOrder: [],
    maxShippingPrice: null,
    currency: ''
  },
  isPackageSizeSelectedManually: false,
  isSecondDayLister: false,
  isPreviousLister: false
};

function deleteError(draft, fieldName) {
  if (draft.ui.errors.byName[fieldName]) {
    delete draft.ui.errors.byName[fieldName];
  }

  remove(draft.ui.errors.names, name => name === fieldName);
}

const setInitialItemData = (draft, action) => {
  var _item$brand, _item$itemAlert, _item$brand2, _item$itemAlert2, _item$brand3;

  const {
    item
  } = action.payload;
  draft.attributes.id = item.id;
  draft.attributes.title = item.title || '';
  draft.attributes.description = item.description || '';
  draft.attributes.isUnisex = item.isUnisex;
  draft.attributes.catalogId = item.catalogId;
  draft.attributes.statusId = item.statusId;
  draft.attributes.sizeId = item.sizeId;
  draft.attributes.colorIds = item.colorIds;
  draft.attributes.price = item.price;
  draft.attributes.brandId = item.brandId;
  draft.attributes.brandTitle = ((_item$brand = item.brand) === null || _item$brand === void 0 ? void 0 : _item$brand.title) || null;
  draft.attributes.isPromoted = item.isPromoted;
  draft.attributes.isbn = item.isbn;
  draft.attributes.packageSize.id = item.packageSizeId;
  draft.attributes.packageSize.custom.domestic = item.domesticShipmentPrice;
  draft.attributes.packageSize.custom.international = item.internationalShipmentPrice;
  draft.attributes.videoGameRatingId = item.videoGameRatingId;
  draft.attributes.measurementLength = item.measurementLength;
  draft.attributes.measurementWidth = item.measurementWidth;
  draft.attributes.alertType = ((_item$itemAlert = item.itemAlert) === null || _item$itemAlert === void 0 ? void 0 : _item$itemAlert.itemAlertType) || null;
  draft.previousAttributes.id = item.id;
  draft.previousAttributes.title = item.title || '';
  draft.previousAttributes.description = item.description || '';
  draft.previousAttributes.catalogId = item.catalogId;
  draft.previousAttributes.statusId = item.statusId;
  draft.previousAttributes.sizeId = item.sizeId;
  draft.previousAttributes.colorIds = item.colorIds;
  draft.previousAttributes.price = item.price;
  draft.previousAttributes.brandId = item.brandId;
  draft.previousAttributes.brandTitle = ((_item$brand2 = item.brand) === null || _item$brand2 === void 0 ? void 0 : _item$brand2.title) || null;
  draft.previousAttributes.isbn = item.isbn;
  draft.previousAttributes.packageSize.id = item.packageSizeId;
  draft.previousAttributes.packageSize.custom.domestic = item.domesticShipmentPrice;
  draft.previousAttributes.packageSize.custom.international = item.internationalShipmentPrice;
  draft.previousAttributes.videoGameRatingId = item.videoGameRatingId;
  draft.previousAttributes.measurementLength = item.measurementLength;
  draft.previousAttributes.measurementWidth = item.measurementWidth;
  draft.previousAttributes.alertType = ((_item$itemAlert2 = item.itemAlert) === null || _item$itemAlert2 === void 0 ? void 0 : _item$itemAlert2.itemAlertType) || null;
  draft.formData.brands.isLuxury = ((_item$brand3 = item.brand) === null || _item$brand3 === void 0 ? void 0 : _item$brand3.isLuxury) || null;
  draft.formData.bookDetails = item.bookDetails;
  draft.ui.canBumpItem = item.canPushUp;
  draft.ui.screenName = item.isDraft ? Screen.ItemDraft : Screen.ItemEdit;
};

const setCurrency = (draft, action) => {
  draft.configuration.currency = action.payload.currency;
};

const setStatusId = (draft, action) => {
  draft.attributes.statusId = action.payload.id;
  deleteError(draft, FieldName.Status);
};

const setSizeId = (draft, action) => {
  draft.attributes.sizeId = action.payload.id;
  deleteError(draft, FieldName.Size);
};

const setCatalogId = (draft, action) => {
  draft.attributes.catalogId = action.payload.id;
  deleteError(draft, FieldName.Catalog);
};

const setColorIds = (draft, action) => {
  const {
    ids
  } = action.payload;
  draft.attributes.colorIds = ids;
  deleteError(draft, FieldName.Color);
};

const toggleIsUnisex = draft => {
  draft.attributes.isUnisex = !draft.attributes.isUnisex;
};

const setBrand = (draft, action) => {
  draft.attributes.brandId = action.payload.id;
  draft.attributes.brandTitle = action.payload.title;
  draft.formData.brands.isLuxury = action.payload.isLuxury;
  deleteError(draft, FieldName.Brand);
};

const setTitle = (draft, action) => {
  draft.attributes.title = action.payload.title;
  deleteError(draft, FieldName.Title);
};

const setPrice = (draft, action) => {
  draft.attributes.price = action.payload.price;
  deleteError(draft, FieldName.Price);
};

const setDescription = (draft, _ref) => {
  let {
    payload
  } = _ref;
  draft.attributes.description = payload.description;
  deleteError(draft, FieldName.Description);
};

const setIsAuthenticityProofModalOpen = (draft, action) => {
  draft.ui.isAuthenticityProofModalOpen = action.payload.isOpen;
};

const setAuthenticityModalContent = (draft, action) => {
  draft.ui.authenticityModal.content = action.payload.content;
};

const setIsAuthenticityModalOpen = (draft, action) => {
  draft.ui.authenticityModal.isOpen = action.payload.isOpen;
};

const setConfiguration = (draft, action) => {
  const {
    payload: {
      configuration
    }
  } = action;
  draft.configuration.catalogFieldsOrder = configuration.catalog_group_fields;
  draft.configuration.maxShippingPrice = configuration.max_custom_shipping_price;
};

const setIsOfflineVerificationEligible = (draft, action) => {
  draft.isOfflineVerificationEligible = action.payload.eligible;
};

const setIsOfflineVerificationModalOpen = (draft, action) => {
  draft.ui.isOfflineVerificationModalOpen = action.payload.isOpen;
};

const setFieldErrors = (draft, action) => {
  const {
    fieldErrors
  } = action.payload;
  fieldErrors.forEach(error => {
    draft.ui.errors.byName[error.field] = error.value;
  });
  draft.ui.errors.names = fieldErrors.map(error => error.field);
};

const setFieldError = (draft, action) => {
  const {
    fieldError
  } = action.payload;
  draft.ui.errors.byName[fieldError.field] = fieldError.value;
  if (draft.ui.errors.names.includes(fieldError.field)) return;
  draft.ui.errors.names.push(fieldError.field);
};

const removeFieldError = (draft, action) => {
  deleteError(draft, action.payload.fieldName);
};

const setPhotoTips = (draft, action) => {
  const {
    photoTips
  } = action.payload;
  setNormalizedData(draft.formData.photoTips, photoTips);
};

const setSizeGroups = (draft, action) => {
  const {
    sizeGroups
  } = action.payload;
  setNormalizedData(draft.formData.sizeGroups, sizeGroups);
};

const toggleIsShippingOptionsModalOpen = draft => {
  draft.ui.shippingOptions.isModalOpen = !draft.ui.shippingOptions.isModalOpen;
};

const setIsLuxuryItemModalOpen = (draft, action) => {
  draft.ui.isLuxuryItemModalOpen = action.payload.isOpen;
};

const showMissingPostalCode = draft => {
  draft.ui.showMissingPostalCode = true;
};

const hideMissingPostalCode = draft => {
  draft.ui.showMissingPostalCode = false;
};

const fetchShippingOptionsRequest = draft => {
  draft.ui.shippingOptions.uiState = UiStateEnum.Pending;
};

const fetchShippingOptionsFailure = draft => {
  draft.ui.shippingOptions.uiState = UiStateEnum.Failure;
};

const fetchShippingOptionsSuccess = (draft, action) => {
  const {
    shippingOptions
  } = action.payload;
  draft.formData.shippingOptions = shippingOptions;
  draft.ui.shippingOptions.uiState = UiStateEnum.Success;
};

const setUiState = (draft, action) => {
  const {
    uiState
  } = action.payload;
  draft.ui.uiState = uiState;
};

const setVideoGameRatings = (draft, action) => {
  setNormalizedData(draft.formData.videoGameRatings, action.payload);
};

const setIsFormDisabled = (draft, action) => {
  const {
    isFormDisabled
  } = action.payload;
  draft.ui.isFormDisabled = isFormDisabled;
};

const disableItemUpload = draft => {
  draft.ui.isFormDisabled = true;
};

const enableItemUpload = draft => {
  draft.ui.isFormDisabled = false;
};

const toggleIsSimilarItemsModalOpen = draft => {
  draft.ui.price.isSimilarItemsModalOpen = !draft.ui.price.isSimilarItemsModalOpen;
};

const setPriceSuggestions = (draft, action) => {
  const {
    min,
    max,
    hasSimilarItems,
    currency,
    displayType
  } = action.payload.suggestions;
  draft.formData.priceSuggestions.min = min;
  draft.formData.priceSuggestions.max = max;
  draft.formData.priceSuggestions.currency = currency;
  draft.ui.price.hasSimilarItems = hasSimilarItems;
  draft.ui.price.displayType = displayType;
};

const clearPriceSuggestions = draft => {
  draft.formData.priceSuggestions = initialFormDataState.priceSuggestions;
  draft.ui.price = initialUiState.price;
};

const setBrands = (draft, action) => {
  const {
    brands
  } = action.payload;
  setNormalizedData(draft.formData.brands, brands);
};

const setCatalogs = (draft, action) => {
  const {
    catalogs
  } = action.payload;
  setNormalizedData(draft.formData.catalogs, catalogs); // Starting ids for catalogs at root level (with no parent id)

  const initialCatalogIds = [];
  catalogs.forEach(catalog => {
    if (!catalog.parentId) {
      initialCatalogIds.push(catalog.id);
    }
  });
  draft.formData.catalogs.initialIds = initialCatalogIds;
};

const setColors = (draft, action) => {
  const {
    colors
  } = action.payload;
  setNormalizedData(draft.formData.colors, colors);
  draft.ui.color.uiState = UiStateEnum.Success;
};

const setSizes = (draft, action) => {
  const {
    sizes
  } = action.payload;
  setNormalizedData(draft.formData.sizes, sizes);
};

const setSuggestions = (draft, action) => {
  const {
    colors,
    sizes,
    brands,
    catalogs
  } = action.payload;
  draft.formData.catalogs.suggested = catalogs;
  setNormalizedData(draft.formData.colors.suggested, colors);
  setNormalizedData(draft.formData.sizes.suggested, sizes);
  setNormalizedData(draft.formData.brands.suggested, brands);
};

const setColorPendingState = draft => {
  draft.ui.color.uiState = UiStateEnum.Pending;
};

const setColorFailureState = draft => {
  draft.ui.color.uiState = UiStateEnum.Failure;
};

const setStatuses = (draft, action) => {
  const {
    statuses
  } = action.payload;
  setNormalizedData(draft.formData.statuses, statuses);
};

const updateAssignedPhotoIds = (draft, action) => {
  const {
    currentIds
  } = action.payload;
  draft.attributes.assignedPhotos = currentIds;
  draft.ui.isFormDisabled = false;
};

const setAssignedPhotos = (draft, action) => {
  const {
    photos
  } = action.payload;
  photos.forEach(_ref2 => {
    let {
      id,
      isWeb
    } = _ref2;
    draft.previousAttributes.assignedPhotos.push(id);
    draft.attributes.assignedPhotos.push(id);

    if (isWeb) {
      draft.ui.containsWebPhoto = true;
    }
  });
  deleteError(draft, FieldName.Photos);
};

const addAssignedPhotos = (draft, action) => {
  const {
    photos
  } = action.payload;
  photos.forEach(_ref3 => {
    let {
      id,
      isWeb
    } = _ref3;
    draft.attributes.assignedPhotos.push(id);

    if (isWeb) {
      draft.ui.containsWebPhoto = true;
    }
  });
  draft.ui.isFormDisabled = false;
  deleteError(draft, FieldName.Photos);
};

const removeAssignedPhoto = (draft, action) => {
  const {
    id
  } = action.payload;
  remove(draft.attributes.assignedPhotos, photoId => photoId === id);
};

const changeAssignedPhotoOrder = (draft, action) => {
  const {
    oldIndex,
    newIndex
  } = action.payload;
  const reorderedAssignedPhotos = arrayMove(draft.attributes.assignedPhotos, oldIndex, newIndex);
  draft.attributes.assignedPhotos = reorderedAssignedPhotos;
};

const fetchDynamicAttributesSuccess = (draft, action) => {
  const {
    dynamicAttributes
  } = action.payload;
  draft.formData.dynamicAttributes.byName = {};
  dynamicAttributes.forEach(attr => {
    draft.formData.dynamicAttributes.byName[attr.code] = attr;
  });
  draft.ui.dynamicAttributes.uiState = UiStateEnum.Success;
};

const fetchDynamicAttributesFailure = draft => {
  draft.ui.dynamicAttributes.uiState = UiStateEnum.Failure;
};

const setDynamicAttributesUiState = (draft, action) => {
  const {
    uiState
  } = action.payload;
  draft.ui.dynamicAttributes.uiState = uiState;
};

const setSelectedDynamicAttributeValues = (draft, action) => {
  const {
    selectedValues
  } = action.payload;
  selectedValues.forEach(value => {
    draft.attributes.catalog.attributes.byName[value.field] = value.value;
  });
  draft.attributes.catalog.attributes.names = selectedValues.map(value => value.field);
};

const setSelectedDynamicAttributeValue = (draft, action) => {
  const {
    field,
    value
  } = action.payload;
  draft.attributes.catalog.attributes.byName[field] = value;
  if (draft.attributes.catalog.attributes.names.includes(field)) return;
  draft.attributes.catalog.attributes.names.push(field);
};

const setIsPackageSizeManuallySelected = (draft, action) => {
  const {
    isPackageSizeManuallySelected
  } = action.payload;
  draft.isPackageSizeSelectedManually = isPackageSizeManuallySelected;
};

const setIsbnPendingState = draft => {
  draft.ui.isbn.uiState = UiStateEnum.Pending;
};

const setIsbn = (draft, action) => {
  const {
    isbn
  } = action.payload;
  draft.attributes.isbn = isbn;
  draft.formData.bookDetails = null;
  draft.ui.isbn.uiState = UiStateEnum.Idle;
};

const setBookDetails = (draft, action) => {
  const {
    bookDetails
  } = action.payload;
  draft.formData.bookDetails = bookDetails;
  draft.ui.isbn.uiState = UiStateEnum.Success;
};

const setPackageSizeId = (draft, action) => {
  draft.attributes.packageSize.id = action.payload.id;
  deleteError(draft, FieldName.PackageSize);
};

const setDomesticShippingPrice = (draft, action) => {
  draft.attributes.packageSize.custom.domestic = action.payload.price;
  deleteError(draft, FieldName.DomesticShipmentPrice);
};

const setInternationalShippingPrice = (draft, action) => {
  draft.attributes.packageSize.custom.international = action.payload.price;
  deleteError(draft, FieldName.InternationalShipmentPrice);
};

const setFeedbackId = (draft, action) => {
  const {
    id
  } = action.payload;
  draft.attributes.feedbackId = id;
  draft.previousAttributes.feedbackId = id;
};

const setShouldShowSizeModal = (draft, action) => {
  draft.formData.shouldShowSizeModal = action.payload.shouldShowSizeModal;
};

const setVideoGameRatingId = (draft, action) => {
  draft.attributes.videoGameRatingId = action.payload.id;
  deleteError(draft, FieldName.VideoGameRating);
};

const toggleIsPushUpChecked = draft => {
  draft.attributes.isPushUpChecked = !draft.attributes.isPushUpChecked;
};

const setMeasurementWidth = (draft, action) => {
  draft.attributes.measurementWidth = action.payload.width;
  deleteError(draft, FieldName.MeasurementWidth);
};

const setMeasurementLength = (draft, action) => {
  draft.attributes.measurementLength = action.payload.length;
  deleteError(draft, FieldName.MeasurementLength);
};

const setIsSecondDayLister = (draft, action) => {
  draft.isSecondDayLister = action.payload.isSecondDayLister;
};

const setIsPreviousLister = (draft, action) => {
  draft.isPreviousLister = action.payload.isPreviousLister;
};

const itemUploadSlice = createSlice({
  name: stateName,
  initialState,
  reducers: {
    setInitialItemData,
    toggleIsUnisex,
    setCatalogId,
    setColorIds,
    setTitle,
    setDescription,
    setFieldError,
    setFieldErrors,
    removeFieldError,
    setStatusId,
    setSizeId,
    setStatuses,
    setBrands,
    setCatalogs,
    setColors,
    setSizes,
    setPrice,
    setSuggestions,
    setColorPendingState,
    setColorFailureState,
    setBrand,
    setIsAuthenticityProofModalOpen,
    setAuthenticityModalContent,
    setIsAuthenticityModalOpen,
    fetchColorsSuccess: setColors,
    fetchColorsRequest: setColorPendingState,
    fetchColorsFailure: setColorFailureState,
    setIsFormDisabled,
    showMissingPostalCode,
    hideMissingPostalCode,
    toggleIsShippingOptionsModalOpen,
    setIsLuxuryItemModalOpen,
    fetchShippingOptionsRequest,
    fetchShippingOptionsFailure,
    fetchShippingOptionsSuccess,
    setUiState,
    setPhotoTips,
    setSizeGroups,
    setVideoGameRatings,
    setPriceSuggestions,
    clearPriceSuggestions,
    updateAssignedPhotoIds,
    toggleIsSimilarItemsModalOpen,
    setIsOfflineVerificationModalOpen,
    setIsOfflineVerificationEligible,
    fetchDynamicAttributesSuccess,
    fetchDynamicAttributesFailure,
    setDynamicAttributesUiState,
    setSelectedDynamicAttributeValues,
    setSelectedDynamicAttributeValue,
    setConfiguration,
    setIsPackageSizeManuallySelected,
    setIsSecondDayLister,
    setIsPreviousLister,
    setIsbn,
    setBookDetails,
    setIsbnPendingState,
    setPackageSizeId,
    setDomesticShippingPrice,
    setInternationalShippingPrice,
    setVideoGameRatingId,
    setMeasurementLength,
    setMeasurementWidth,
    setShouldShowSizeModal,
    setFeedbackId,
    toggleIsPushUpChecked,
    setCurrency
  },
  extraReducers: {
    [mediaUploadActions.fetchPhotosSuccess.type]: setAssignedPhotos,
    [mediaUploadActions.uploadPhotoFiles.type]: disableItemUpload,
    [mediaUploadActions.submitEditedPhoto.type]: disableItemUpload,
    [mediaUploadActions.uploadPhotoSuccess.type]: addAssignedPhotos,
    [mediaUploadActions.uploadPhotoFailure.type]: enableItemUpload,
    [mediaUploadActions.submitEditedPhotoSuccess.type]: updateAssignedPhotoIds,
    [mediaUploadActions.removePhoto.type]: removeAssignedPhoto,
    [mediaUploadActions.changePhotoOrder.type]: changeAssignedPhotoOrder
  }
});
export const {
  actions
} = itemUploadSlice;
export const plug = {
  [stateName]: itemUploadSlice.reducer
};
export default itemUploadSlice.reducer;